import { InvoiceComparisonFields, InvoiceComparisonSummary } from './InvoiceComparison';
import { invoiceComparison } from './invoiceComparison';
import { householdMoneyLabel } from './householdShare';
import { HouseholdShareFields } from './HouseholdShareFields';
import { AcquisitionPaymentFields } from './AcquisitionPaymentFields';
import { SpecialRepaymentFields } from './SpecialRepaymentFields';
import { LoanDrawdownFields } from './LoanDrawdownFields';
import { NumberField, DocumentSelect } from './PlanningFields';
export { NumberField, DocumentSelect } from './PlanningFields';
import { RentalTaxFields } from './RentalTaxFields';
import { useState } from 'react';
import { eur } from './types';
import {
  addMonths,
  costSummary,
  financeProjection,
  financeStatusLabels,
  sourceLabels,
  taskCalendar,
  updateFinanceRequest,
  acquisitionFeeTemplates,
  setFeeMode,
  type FeeKind,
  type CostItem,
  type CostRevision,
  type FinanceRequest,
  type Planning,
  type ProjectTask,
  type UsageUnit,
} from './purchasePlanning';
import type { Document } from './dueDiligence';
import type { GuidedTask } from './guidedTasks';
import { costEvidence, costEvidenceLabels, costNextActions, costRangeLabel, hasCostRange } from './costEvidence';

const id = () => crypto.randomUUID();
function exportCalendar(tasks: ProjectTask[], title: string) {
  const url = URL.createObjectURL(
    new Blob([taskCalendar(tasks, title)], {
      type: 'text/calendar;charset=utf-8',
    }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = 'immobilien-termine.ics';
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function TaskPanel({
  planning,
  documents,
  title,
  onChange,
  tasks,
  onLinkedChange,
  onOpenSource,
  stage = 'assessment',
  focusTaskId = '',
}: {
  planning: Planning;
  focusTaskId?: string;
  documents: Document[];
  title: string;
  onChange: (value: Planning) => void;
  tasks?: GuidedTask[];
  onLinkedChange?: (task: GuidedTask) => void;
  onOpenSource?: (task: GuidedTask) => void;
  stage?: ProjectTask['phase'];
}) {
  const [newTitle, setNewTitle] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const update = (task: ProjectTask) => {
    const linked = tasks?.find((item) => item.id === task.id);
    if (linked && linked.source !== 'manual') {
      onLinkedChange?.({ ...linked, ...task });
      return;
    }
    onChange({
      ...planning,
      tasks: planning.tasks.map((current) =>
        current.id === task.id ? task : current,
      ),
    });
  };
  const all: GuidedTask[] =
    tasks ??
    planning.tasks.map((task) => ({
      ...task,
      source: 'manual',
      sourceId: task.id,
    }));
  const completed = all.filter((task) => task.status === 'done').length;
  const sorted = [...all]
    .filter((task) => showCompleted || task.status !== 'done')
    .sort(
      (a, b) =>
        (a.status === 'done' ? 1 : 0) - (b.status === 'done' ? 1 : 0) ||
        (a.dueDate || '9999').localeCompare(b.dueDate || '9999'),
    );
  const complete = (task: ProjectTask) =>
    update(
      task.repeatMonths > 0 && task.dueDate
        ? {
            ...task,
            status: 'todo',
            dueDate: addMonths(task.dueDate, task.repeatMonths),
            note: `Zuletzt erledigt ${new Date().toLocaleDateString('de-DE')}. ${task.note}`,
          }
        : { ...task, status: task.status === 'done' ? 'todo' : 'done' },
    );
  return (
    <section className="journey-panel">
      <div className="journey-section-heading">
        <div>
          <h2>Was als Nächstes ansteht</h2>
          <p>
            Prüfungen, Kosten, Bankanfragen und Wartungspläne führen ihre nächsten Schritte
            hier zusammen. Änderungen bleiben mit ihrer Quelle verbunden.
          </p>
        </div>
        <button
          className="quiet-button"
          onClick={() => exportCalendar(all, title)}
        >
          Erinnerungen im Kalender
        </button>
      </div>
      <form
        className="journey-add"
        onSubmit={(event) => {
          event.preventDefault();
          if (!newTitle.trim()) return;
          onChange({
            ...planning,
            tasks: [
              ...planning.tasks,
              {
                id: id(),
                title: newTitle.trim(),
                owner: '',
                dueDate: '',
                status: 'todo',
                phase: stage,
                repeatMonths: 0,
                reminderDays: 1,
                documentId: '',
                note: '',
              },
            ],
          });
          setNewTitle('');
        }}
      >
        <input
          aria-label="Neue Aufgabe"
          placeholder="Zum Beispiel: schriftlichen Tankbefund einholen"
          value={newTitle}
          onChange={(event) => setNewTitle(event.target.value)}
        />
        <button>Aufgabe hinzufügen</button>
      </form>
      {completed > 0 && (
        <label className="journey-checkbox">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(event) => setShowCompleted(event.target.checked)}
          />
          {completed} erledigte Schritte anzeigen
        </label>
      )}
      {sorted.length ? (
        sorted.map((task) => (
          <details className={`journey-task ${task.status}`} key={task.id} open={task.id === focusTaskId ? true : undefined}>
            <summary>
              <span className="journey-task-dot" />
              <strong>{task.title}</strong>
              <span>
                {task.source !== 'manual' && (
                  <b className="journey-task-source">
                    {task.source === 'check'
                      ? 'Fachprüfung'
                      : task.source === 'cost'
                        ? 'Kostenplan'
                        : task.source === 'maintenance' ? 'Wartungsplan' : 'Finanzierung'}{' '}
                    ·{' '}
                  </b>
                )}
                {task.status === 'done'
                  ? 'Erledigt'
                  : task.dueDate || 'Termin offen'}{' '}
                · {task.owner || 'Verantwortung offen'}
              </span>
            </summary>
            <fieldset className="journey-linked-fields" disabled={task.source === 'maintenance'}>
            <div className="journey-form-grid">
              <label>
                Aufgabe
                <input
                  value={task.title}
                  readOnly={task.source === 'cost' || task.source === 'finance'}
                  onChange={(event) =>
                    update({ ...task, title: event.target.value })
                  }
                />
              </label>
              <label>
                Verantwortlich
                <input
                  value={task.owner}
                  onChange={(event) =>
                    update({ ...task, owner: event.target.value })
                  }
                />
              </label>
              <label>
                Fällig am
                <input
                  type="date"
                  value={task.dueDate}
                  onChange={(event) =>
                    update({ ...task, dueDate: event.target.value })
                  }
                />
              </label>
              <label>
                Status
                {task.source === 'manual' ? (
                  <>
                    <select
                      aria-label={`Aufgabenstatus ${task.title}`}
                      value={task.status}
                      onChange={(event) =>
                        update({
                          ...task,
                          status: event.target.value as ProjectTask['status'],
                        })
                      }
                    >
                      <option value="todo">Offen</option>
                      <option value="waiting">Warten auf Antwort</option>
                      <option value="done">Erledigt</option>
                    </select>
                  </>
                ) : (
                  <span className="journey-caption">
                    {task.status === 'done'
                      ? 'Erledigt'
                      : task.status === 'waiting'
                        ? 'Antwort ausstehend'
                        : 'Offen'}{' '}
                    · folgt der zugehörigen{' '}
                    {task.source === 'check'
                      ? 'Fachprüfung'
                      : task.source === 'cost'
                        ? 'Kostenposition'
                        : task.source === 'maintenance' ? 'Wartungsplanung' : 'Bankanfrage'}
                    .
                  </span>
                )}
              </label>
              {task.source === 'manual' && (
                <>
                  <NumberField
                    label="Wiederholen alle Monate"
                    value={task.repeatMonths}
                    onChange={(value) =>
                      update({ ...task, repeatMonths: value ?? 0 })
                    }
                    max={120}
                  />
                </>
              )}
              <NumberField
                label="Erinnerung Tage vorher"
                value={task.reminderDays}
                onChange={(value) =>
                  update({ ...task, reminderDays: value ?? 0 })
                }
                max={365}
              />
              {task.source === 'manual' && (
                <DocumentSelect
                  documents={documents}
                  value={task.documentId}
                  onChange={(value) => update({ ...task, documentId: value })}
                />
              )}
            </div>
            <label>
              Nächste Frage / Notiz
              <textarea
                rows={2}
                value={task.note}
                readOnly={task.source === 'cost' || task.source === 'finance'}
                onChange={(event) =>
                  update({ ...task, note: event.target.value })
                }
              />
            </label>
            </fieldset>
            <div className="journey-inline-actions">
              {task.source === 'manual' ? (
                <>
                  <button onClick={() => complete(task)}>
                    {task.repeatMonths
                      ? 'Termin erledigen & nächsten planen'
                      : task.status === 'done'
                        ? 'Wieder öffnen'
                        : 'Erledigt markieren'}
                  </button>
                </>
              ) : (
                <button onClick={() => onOpenSource?.(task)}>
                  {task.source === 'check'
                    ? 'Prüfung & Belege klären'
                    : task.source === 'cost'
                      ? 'Kostenposition öffnen'
                      : task.source === 'maintenance' ? 'Wartungsplan & Nachweise öffnen' : 'Bankanfrage öffnen'}
                </button>
              )}
              {task.source === 'manual' && (
                <button
                  className="quiet-button"
                  onClick={() =>
                    onChange({
                      ...planning,
                      tasks: planning.tasks.filter(
                        (item) => item.id !== task.id,
                      ),
                    })
                  }
                >
                  Entfernen
                </button>
              )}
            </div>
          </details>
        ))
      ) : (
        <div className="journey-empty">
          <h3>Alles beginnt mit dem nächsten guten Schritt.</h3>
          <p>
            Ergänze eigene Termine. Offene Fachfragen, Kosten und Bankanfragen
            erscheinen automatisch, sobald sie für dieses Objekt relevant sind.
          </p>
        </div>
      )}
      <p className="journey-caption">
        Die Kalenderdatei enthält Termine und Erinnerungen zum Importieren. Bei
        geänderten Fristen erneut exportieren. Diese App versendet keine E-Mails
        und führt keine Buchungen aus.
      </p>
    </section>
  );
}
export function CostsPanel({
  planning,
  documents,
  onChange,
  onOpenDocuments,
}: {
  planning: Planning;
  documents: Document[];
  onChange: (value: Planning) => void;
  onOpenDocuments?: () => void;
}) {
  const [newTitle, setNewTitle] = useState('');
  const [filter, setFilter] = useState<'all' | 'range_missing' | 'evidence_missing'>('all');
  const summary = costSummary(planning, documents);
  const activeFilter = summary.total ? filter : 'all';
  const visible = (item: CostItem) => activeFilter === 'all' || ['inspection', 'repair'].includes(item.category)
    && (activeFilter === 'range_missing' ? !hasCostRange(item.revisions.at(-1))
      : hasCostRange(item.revisions.at(-1)) && costEvidence(item, documents) !== 'linked');
  const update = (item: CostItem) =>
    onChange({
      ...planning,
      costs: planning.costs.map((current) =>
        current.id === item.id ? item : current,
      ),
    });
  return (
    <section className="journey-panel">
      <div className="journey-section-heading">
        <div>
          <h2>Aus Unsicherheit wird ein belastbarer Kostenplan</h2>
          <p>
            Jeder neue Befund verbessert den Plan. Frühere Annahmen bleiben
            nachvollziehbar.
          </p>
        </div>
        <strong className="journey-range" aria-label="Kostenstand für Prüfungen und Arbeiten">
          {costRangeLabel(summary, Boolean(planning.finance.budgetReviewed))}
        </strong>
      </div>
      {summary.total > 0 ? <><div className="journey-confidence" role="img" aria-label={`${summary.documented} von ${summary.total} Positionen mit verknüpftem Angebot oder Rechnung`}>
        <div style={{ width: `${summary.documentCoverage * 100}%` }} />
      </div>
      <p className="journey-caption">
        {summary.documented} von {summary.total} Prüfungen und Arbeiten mit
        verknüpftem Angebot oder Rechnung · {summary.unknown} Spannen offen.
        Die Anzeige zählt Dateien und Fundstellen; Umfang und Ausschlüsse prüfst du im Original.
        Kaufpreis und Kaufnebenkosten stehen im Finanzierungsplan.
      </p>
      <div className="journey-cost-filters" role="group" aria-label="Kosten gezielt ergänzen">
        <button type="button" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>Alle Positionen ({planning.costs.length})</button>
        <button type="button" aria-pressed={filter === 'range_missing'} onClick={() => setFilter('range_missing')}>Spannen ergänzen ({summary.unknown})</button>
        <button type="button" aria-pressed={filter === 'evidence_missing'} onClick={() => setFilter('evidence_missing')}>Belege ergänzen ({summary.known - summary.documented})</button>
      </div>
      {activeFilter !== 'all' && <p className="journey-caption" role="status">{planning.costs.filter(visible).length} Prüfungen und Arbeiten in dieser Ansicht. Erwerbskosten findest du unter „Alle Positionen“ und im Finanzierungsplan.</p>}
      </> : <p className="journey-caption">{planning.finance.budgetReviewed
        ? 'Bisher sind keine zusätzlichen Prüfungen oder Arbeiten geplant. Ergänze neue Befunde und Kosten, sobald sie bekannt sind.'
        : 'Welche Fachprüfungen und Arbeiten sind nötig? Erfasse zunächst auch Positionen ohne Kostenspanne. Ein leerer, ungeprüfter Plan ist noch kein Null-Euro-Budget.'}
        {' '}Kaufpreis und Kaufnebenkosten stehen im Finanzierungsplan.</p>}
      <form
        className="journey-add"
        onSubmit={(event) => {
          event.preventDefault();
          if (!newTitle.trim()) return;
          onChange({
            ...planning,
            costs: [
              ...planning.costs,
              {
                id: id(),
                title: newTitle.trim(),
                category: 'repair',
                dueMonth: 0,
                revisions: [],
              },
            ],
          });
          setNewTitle('');
        }}
      >
        <input
          aria-label="Neue Kostenposition"
          placeholder="Zum Beispiel: Tankerneuerung oder Feuchteprüfung"
          value={newTitle}
          onChange={(event) => setNewTitle(event.target.value)}
        />
        <button>Position hinzufügen</button>
      </form>
      {planning.costs.map((item) => (
        <CostEditor
          key={item.id}
          item={item}
          costs={planning.costs}
          documents={documents}
          onChange={update}
          hidden={!visible(item)}
          onOpenDocuments={onOpenDocuments}
        />
      ))}
      {activeFilter !== 'all' && !planning.costs.some(visible) && <p className="journey-empty">In dieser Gruppe ist gerade nichts zu ergänzen. Umfang und Vollständigkeit des Kostenplans weiter prüfen.</p>}
      {!planning.costs.length && (
        <div className="journey-empty">
          <h3>Offene Kosten sind sichtbar.</h3>
          <p>
            Lege Prüfungen und mögliche Arbeiten an, auch wenn ihr noch keine
            Summe kennt.
          </p>
        </div>
      )}
    </section>
  );
}
function CostEditor({
  item,
  costs,
  documents,
  onChange,
  hidden = false,
  onOpenDocuments,
}: {
  item: CostItem;
  costs: CostItem[];
  documents: Document[];
  onChange: (value: CostItem) => void;
  hidden?: boolean;
  onOpenDocuments?: () => void;
}) {
  const latest = item.revisions.at(-1);
  const evidence = costEvidence(item, documents);
  const [draft, setDraft] = useState<CostRevision>({
    id: id(),
    low: null,
    high: null,
    level: 'estimate',
    documentId: '',
    locator: '',
    note: '',
    createdAt: '',
  });
  const [problem, setProblem] = useState('');
  const revise = (patch: Partial<CostRevision>) => setDraft({ ...draft, ...patch, quoteComparison: draft.quoteComparison ? { ...draft.quoteComparison, scopeConfirmed: false } : undefined });
  const commit = () => {
    if (
      !hasCostRange(draft)
    ) {
      setProblem(
        'Eine gültige Brutto-Spanne mit Mindest- und Höchstbetrag fehlt.',
      );
      return;
    }
    if (
      ['quote', 'invoice'].includes(draft.level) &&
      (!documents.some(document => document.id === draft.documentId) || !draft.locator.trim())
    ) {
      setProblem(
        'Angebot oder Rechnung bitte mit Dokument und Fundstelle verknüpfen.',
      );
      return;
    }
    if (draft.quoteComparison && (invoiceComparison(item, draft, documents).state === 'unavailable'
      || draft.quoteComparison.scopeConfirmed && invoiceComparison(item, draft, documents).state !== 'compared')) {
      setProblem('Das Angebot kann nicht eindeutig mit dieser Rechnung verglichen werden. Quelle und Umfang prüfen.');
      return;
    }
    if (item.revisions.length >= 50) { setProblem('Der Verlauf enthält bereits 50 Kostenstände. Lege bei Bedarf eine neue Position an; frühere Stände bleiben erhalten.'); return; }
    onChange({
      ...item,
      revisions: [
        ...item.revisions,
        { ...draft, id: id(), createdAt: new Date().toISOString() },
      ],
    });
    setProblem('');
    setDraft({ ...draft, note: '', quoteComparison: undefined });
  };
  return (
    <details className="journey-cost" hidden={hidden}>
      <summary>
        <strong>{item.title}</strong>
        <span>
          {hasCostRange(latest)
            ? `${eur(latest.low)} – ${eur(latest.high)} · ${sourceLabels[latest.level]}`
            : 'Kosten noch offen'}
        </span>
      </summary>
      <div className="journey-cost-next" data-evidence={evidence}>
        <strong>{costEvidenceLabels[evidence]}</strong>
        <p>{costNextActions[evidence]}</p>
        {onOpenDocuments && ['document_missing', 'location_missing', 'linked'].includes(evidence) &&
          <button type="button" className="quiet-button" onClick={onOpenDocuments}>Unterlagen öffnen</button>}
      </div>
      <div className="journey-form-grid">
        <label>
          Bezeichnung
          <input
            value={item.title}
            onChange={(event) =>
              onChange({ ...item, title: event.target.value })
            }
          />
        </label>
        <label>
          Art
          <select
            aria-label="Art der Kostenposition"
            value={item.category}
            onChange={(event) =>
              onChange({
                ...item,
                category: event.target.value as CostItem['category'],
                feeKind:
                  event.target.value === 'fees'
                    ? (item.feeKind ?? 'other')
                    : undefined,
              })
            }
          >
            <option value="repair">Arbeit / Sanierung</option>
            <option value="inspection">Fachprüfung / Beratung</option>
            <option value="fees">Erwerbs- / Finanzierungskosten</option>
          </select>
        </label>
        <NumberField
          label="Geplant im Monat nach Kauf"
          value={item.dueMonth}
          onChange={(value) => onChange({ ...item, dueMonth: value ?? 0 })}
          max={360}
        />
      </div>
      {item.category === 'fees' && (
        <label>
          Kostenart
          <select
            aria-label="Kostenart"
            value={item.feeKind ?? 'other'}
            onChange={(event) =>
              onChange({ ...item, feeKind: event.target.value as FeeKind })
            }
          >
            {acquisitionFeeTemplates.map((template) => (
              <option
                key={template.id}
                value={template.id}
                disabled={
                  template.id !== 'other' &&
                  costs.some(
                    (cost) =>
                      cost.id !== item.id &&
                      cost.category === 'fees' &&
                      cost.feeKind === template.id,
                  )
                }
              >
                {template.title}
              </option>
            ))}
          </select>
        </label>
      )}
      <h3>Neuen Kenntnisstand hinzufügen</h3>
      <div className="journey-form-grid">
        <NumberField
          label="Brutto mindestens"
          value={draft.low}
          onChange={(value) => revise({ low: value })}
          suffix="€"
          nullable
        />
        <NumberField
          label="Brutto höchstens"
          value={draft.high}
          onChange={(value) => revise({ high: value })}
          suffix="€"
          nullable
        />
        <label>
          Grundlage
          <select
            aria-label="Kostengrundlage"
            value={draft.level}
            onChange={(event) =>
              setDraft({
                ...draft,
                level: event.target.value as CostRevision['level'],
                quoteComparison: undefined,
              })
            }
          >
            {Object.entries(sourceLabels)
              .filter(([key]) => key !== 'unknown')
              .map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
          </select>
        </label>
        <DocumentSelect
          documents={documents}
          value={draft.documentId}
          onChange={(value) => revise({ documentId: value })}
        />
        <label>
          Fundstelle
          <input
            value={draft.locator}
            onChange={(event) =>
              revise({ locator: event.target.value })
            }
            placeholder="Seite / Abschnitt / Zeitstempel"
          />
        </label>
      </div>
      <label>
        Umfang, Ausschlüsse und offene Abhängigkeiten
        <textarea
          rows={2}
          value={draft.note}
          onChange={(event) => revise({ note: event.target.value })}
        />
      </label>
      <InvoiceComparisonFields item={item} draft={draft} documents={documents} onChange={setDraft} />
      <button onClick={commit}>Kostenstand übernehmen</button>
      {problem && (
        <p role="alert" className="state error">
          {problem}
        </p>
      )}
      <ol className="journey-history">
        {[...item.revisions].reverse().map((revision) => (
          <li key={revision.id}>
            <strong>
              {eur(revision.low)} – {eur(revision.high)}
            </strong>
            <span>
              {sourceLabels[revision.level]} ·{' '}
              {new Date(revision.createdAt).toLocaleDateString('de-DE')} ·{' '}
              {revision.locator || 'ohne Fundstelle'}
            </span>
            {revision.documentId && <small>{documents.find(document => document.id === revision.documentId)?.name ?? 'Verknüpfte Datei nicht verfügbar'}</small>}
            <p>{revision.note}</p>
            <InvoiceComparisonSummary item={item} revision={revision} documents={documents} />
          </li>
        ))}
      </ol>
    </details>
  );
}
export function FinancingPanel({
  planning,
  documents,
  onChange,
}: {
  planning: Planning;
  documents: Document[];
  onChange: (value: Planning) => void;
}) {
  const result = financeProjection(planning);
  const f = planning.finance;
  const money=f.householdShare===undefined&&!planning.requests.some(r=>r.specialRepayments!==undefined)?eur:householdMoneyLabel;
  const [newLender, setNewLender] = useState('');
  const finance = (patch: Partial<Planning['finance']>) =>
    onChange({ ...planning, finance: { ...f, ...patch } });
  const request = (item: FinanceRequest) =>
    onChange(updateFinanceRequest(planning, item));
  const unit = (item: UsageUnit) =>
    onChange({
      ...planning,
      units: planning.units.map((current) =>
        current.id === item.id ? item : current,
      ),
    });
  return (
    <section className="journey-panel">
      <div className="journey-section-heading">
        <div>
          <h2>Finanzierung mit dem echten Objekt planen</h2>
          <p>
            Mehrere Anfragen parallel, private und vermietete Teile getrennt,
            spätere Mieten sichtbar.
          </p>
        </div>
      </div>
      <details className="journey-cost" open>
        <summary>
          <strong>Erwerbskosten Schritt für Schritt verfeinern</strong>
          <span>
            {money(result.acquisition.low)} – {money(result.acquisition.high)} ·{' '}
            {result.acquisition.unknown} offen
          </span>
        </summary>
        <p>
          Einzelkosten verfeinern die frühere Pauschale. Solange Positionen
          offen sind, bleibt deren Rest als Planungsreserve sichtbar. Sobald
          alles erfasst ist, ersetzt die Summe der Einzelkosten die Pauschale.
          Grundschuld- und Finanzierungskosten sind vom Eigentumserwerb
          getrennt.
        </p>
        {result.acquisition.reserve > 0 && (
          <p className="journey-caption">
            Davon {money(result.acquisition.reserve)} noch unaufgeschlüsselte
            Reserve. Der obere Planwert ist keine garantierte Obergrenze.
          </p>
        )}
        <label>
          Grundlage der Erwerbskosten
          <select
            aria-label="Erwerbskostenmodus"
            value={result.acquisition.mode}
            onChange={(event) =>
              onChange(
                setFeeMode(
                  planning,
                  event.target.value as 'allowance' | 'itemized',
                ),
              )
            }
          >
            <option value="allowance">Pauschale Planungsannahme</option>
            <option value="itemized">Einzelne Kosten und Quellen</option>
          </select>
        </label>
        {result.acquisition.mode === 'itemized' &&
          planning.costs
            .filter((cost) => cost.category === 'fees')
            .map((item) => (
              <CostEditor
                key={item.id}
                item={item}
                costs={planning.costs}
                documents={documents}
                onChange={(cost) =>
                  onChange({
                    ...planning,
                    costs: planning.costs.map((current) =>
                      current.id === cost.id ? cost : current,
                    ),
                  })
                }
              />
            ))}
      </details>
      <HouseholdShareFields planning={planning} onChange={onChange}/>
      <div className="journey-metrics">
        <div>
          <span>Planungsdarlehen dieses Haushalts</span>
          <strong>{money(result.householdShareReady ? result.loan : null)}</strong>
        </div>
        <div>
          <span>Darlehenszahlung im Kaufmonat</span>
          <strong>{money(result.payment)}</strong>
          {!result.complete && <small>Vorläufiger Teilplan</small>}
        </div>
        <div>
          <span>Offene Finanzierungslücke</span>
          <strong>{money(result.householdShareReady ? Math.max(0,result.fundingGap) : null)}</strong>
        </div>
      </div>
      {f.householdShare!==undefined&&<p className="journey-caption">Anteiliger Anfangsbedarf dieses Haushalts: {money(result.reviewedBudgetCents===null?null:result.reviewedBudgetCents/100)} aus {money(result.reviewedBudgetCents===null?null:result.wholeInitialBudget)} Gesamtbudget. Eigene Darlehen und eigene Mittel müssen diesen Bedarf centgenau decken. Ganze Centbeträge verwenden.</p>}
      {result.loans.some(loan => loan.staged) && <p className="journey-caption">Vorfinanzierung späterer Abrufe vor Berücksichtigung des Zahlungsplans: {money(!result.householdShareReady || result.initialBridgeCents === null ? null : result.initialBridgeCents / 100)}. Die Monatsrate zu Beginn kann während der Abrufphase eine reine Zinszahlung sein.</p>}
      <div className="journey-form-grid">
        <NumberField
          label="Geplanter Kaufpreis"
          value={f.purchasePrice}
          onChange={(value) => finance({ purchasePrice: value })}
          suffix="€"
          nullable
        />
        <NumberField
          label="Kaufnebenkosten, Annahme"
          value={f.purchaseFeesPercent}
          onChange={(value) => finance({ purchaseFeesPercent: value ?? 0 })}
          suffix="%"
          max={100}
        />
        <NumberField
          label="Eigenkapital für dieses Objekt"
          value={f.equity}
          onChange={(value) => finance({ equity: value })}
          suffix="€"
          nullable
        />
        <NumberField
          label="Davon als Reserve behalten"
          value={f.retainedReserve}
          onChange={(value) => finance({ retainedReserve: value ?? 0 })}
          suffix="€"
        />
        <NumberField
          label="Planungszins ohne Angebot"
          value={f.assumedInterestRate}
          onChange={(value) => finance({ assumedInterestRate: value ?? 0 })}
          suffix="%"
          max={30}
        />
        <NumberField
          label="Anfängliche Tilgung"
          value={f.assumedRepaymentRate}
          onChange={(value) => finance({ assumedRepaymentRate: value ?? 0 })}
          suffix="%"
          max={100}
        />
      </div>
      <label className="journey-checkbox">
        <input
          type="checkbox"
          checked={Boolean(f.budgetReviewed)}
          onChange={(event) =>
            finance({ budgetReviewed: event.target.checked })
          }
        />
        Ich habe Prüfkosten, Arbeiten, Reserven und Erwerbskosten vollständig
        erfasst; fehlende oder nicht anfallende Positionen bewusst geklärt.
      </label>
      <p className="journey-caption">
        Die Rechnung nutzt obere bekannte Kostenspannen. Unbekannte Beträge
        bleiben außerhalb der Summe und müssen vor einer belastbaren
        Entscheidung geklärt werden.
      </p>
      <AcquisitionPaymentFields planning={planning} onChange={onChange} />
      <h3>Banken & Finanzierungsanfragen</h3>
      <label>
        Finanzierung planen
        <select
          aria-label="Finanzierungsauswahl"
          value={f.financingMode ?? 'single'}
          onChange={(event) =>
            finance({
              financingMode: event.target.value as 'single' | 'combined',
            })
          }
        >
          <option value="single">Ein Angebot vergleichen</option>
          <option value="combined">
            Mehrere Darlehensbausteine kombinieren
          </option>
        </select>
      </label>
      {f.financingMode === 'combined' && (
        <p className="journey-caption">
          Wähle ausdrücklich die gemeinsam benötigten Bausteine. Alternative
          Vollfinanzierungen mehrerer Banken sind keine zusätzlichen Darlehen.
        </p>
      )}
      <form
        className="journey-add"
        onSubmit={(event) => {
          event.preventDefault();
          if (!newLender.trim()) return;
          onChange({
            ...planning,
            requests: [
              ...planning.requests,
              {
                id: id(),
                lender: newLender.trim(),
                contact: '',
                status: 'draft',
                dueDate: '',
                loanAmount: null,
                interestRate: null,
                repaymentRate: null,
                fixedYears: null,
                effectiveRate: null,
                documentId: '',
                note: '',
              },
            ],
          });
          setNewLender('');
        }}
      >
        <input
          aria-label="Neue Finanzierungsanfrage"
          placeholder="Bank oder Vermittler"
          value={newLender}
          onChange={(event) => setNewLender(event.target.value)}
        />
        <button>Anfrage anlegen</button>
      </form>
      {planning.requests.map((item) => (
        <details className="journey-request" key={item.id}>
          <summary>
            <strong>{item.lender}</strong>
            <span>
              {financeStatusLabels[item.status]} ·{' '}
              {item.interestRate === null
                ? 'Zins offen'
                : `${item.interestRate}%`}{' '}
              {(
                f.financingMode === 'combined'
                  ? f.selectedRequestIds?.includes(item.id)
                  : f.selectedRequestId === item.id
              )
                ? '· im Plan'
                : ''}
            </span>
          </summary>
          <div className="journey-form-grid">
            <label>
              Bank / Finanzierungsbaustein
              <input
                value={item.lender}
                onChange={(event) =>
                  request({ ...item, lender: event.target.value })
                }
              />
            </label>
            <label>
              Ansprechpartner
              <input
                value={item.contact}
                onChange={(event) =>
                  request({ ...item, contact: event.target.value })
                }
              />
            </label>
            <label>
              Status
              <select
                value={item.status}
                aria-label={`Status Finanzierungsanfrage ${item.lender}`}
                onChange={(event) =>
                  request({
                    ...item,
                    status: event.target.value as FinanceRequest['status'],
                  })
                }
              >
                {Object.entries(financeStatusLabels).map(([key, label]) => (
                  <option value={key} key={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Nächste Antwort bis
              <input
                type="date"
                value={item.dueDate}
                onChange={(event) =>
                  request({ ...item, dueDate: event.target.value })
                }
              />
            </label>
            <NumberField
              label="Darlehensbetrag"
              value={item.loanAmount}
              onChange={(value) => request({ ...item, loanAmount: value })}
              suffix="€"
              nullable
            />
            <NumberField
              label="Sollzins"
              value={item.interestRate}
              onChange={(value) => request({ ...item, interestRate: value })}
              suffix="%"
              nullable
              max={30}
            />
            <NumberField
              label="Tilgung im Angebot"
              value={item.repaymentRate}
              onChange={(value) => request({ ...item, repaymentRate: value })}
              suffix="%"
              nullable
              max={100}
            />
            <NumberField
              label="Effektivzins"
              value={item.effectiveRate}
              onChange={(value) => request({ ...item, effectiveRate: value })}
              suffix="%"
              nullable
              max={30}
            />
            <NumberField
              label="Zinsbindung Jahre"
              value={item.fixedYears}
              onChange={(value) => request({ ...item, fixedYears: value })}
              nullable
              max={40}
            />
            <NumberField
              label="Anschlusszins, Szenario"
              value={item.refinanceInterestRate ?? null}
              onChange={(value) =>
                request({ ...item, refinanceInterestRate: value })
              }
              suffix="%"
              nullable
              max={30}
            />
            <NumberField
              label="Tilgung nach Zinsbindung, Szenario"
              value={item.refinanceRepaymentRate ?? null}
              onChange={(value) =>
                request({ ...item, refinanceRepaymentRate: value })
              }
              suffix="%"
              nullable
              max={100}
            />
            <LoanDrawdownFields request={item} onChange={request} />
            <SpecialRepaymentFields request={item} onChange={request} />
            <DocumentSelect
              documents={documents}
              value={item.documentId}
              onChange={(value) => request({ ...item, documentId: value })}
            />
            <label>
              Mittelverwendung
              <select
                value={item.loanUse ?? 'mixed'}
                onChange={(event) =>
                  request({
                    ...item,
                    loanUse: event.target.value as FinanceRequest['loanUse'],
                    useReviewed: false,
                  })
                }
              >
                <option value="mixed">Gemischt / Flächenschlüssel</option>
                <option value="private">Nur privat genutzter Teil</option>
                <option value="rental">Nur vermieteter Teil</option>
              </select>
            </label>
            <DocumentSelect
              label="Beleg zur Mittelverwendung"
              documents={documents}
              value={item.useDocumentId ?? ''}
              onChange={(value) =>
                request({ ...item, useDocumentId: value, useReviewed: false })
              }
            />
            <label>
              Fundstelle zur Mittelverwendung
              <input
                value={item.useLocator ?? ''}
                onChange={(event) =>
                  request({
                    ...item,
                    useLocator: event.target.value,
                    useReviewed: false,
                  })
                }
              />
            </label>
          </div>
          <p className="journey-caption">
            Der Anschlussplan beginnt nach der eingetragenen Zinsbindung. Seine
            Rate basiert auf der dann verbleibenden Schuld und gilt bis zum Ende
            des Planungshorizonts. Fehlende Anschlussangaben bleiben Annahmen;
            ein zukünftiger Zinssatz ist keine Bankzusage.
          </p>
          {(item.loanUse ?? 'mixed') !== 'mixed' && (
            <label className="journey-checkbox">
              <input
                type="checkbox"
                disabled={!item.useDocumentId || !item.useLocator?.trim()}
                checked={Boolean(item.useReviewed)}
                onChange={(event) =>
                  request({ ...item, useReviewed: event.target.checked })
                }
              />
              Tatsächliche / geplante wirtschaftliche Mittelverwendung anhand
              des Belegs geprüft. Eine Darlehensbezeichnung allein genügt nicht.
            </label>
          )}
          <label>
            Benötigte Unterlagen, Bedingungen & nächste Frage
            <textarea
              rows={2}
              value={item.note}
              onChange={(event) =>
                request({ ...item, note: event.target.value })
              }
            />
          </label>
          {item.termSources && Object.keys(item.termSources).length > 0 && (
            <details className="journey-transcript">
              <summary>Quellen der übernommenen Konditionen</summary>
              {Object.entries(item.termSources).map(
                ([field, source]) =>
                  source && (
                    <blockquote key={field}>
                      <strong>
                        {field === 'loanAmount'
                          ? 'Darlehensbetrag'
                          : field === 'interestRate'
                            ? 'Sollzins'
                            : 'Tilgung'}
                      </strong>
                      <p>{source.quote}</p>
                      <cite>
                        {documents.find(
                          (document) => document.id === source.documentId,
                        )?.name ?? 'Quelldokument'}{' '}
                        · {source.locator}
                      </cite>
                    </blockquote>
                  ),
              )}
            </details>
          )}
          {f.financingMode === 'combined' ? (
            <label className="journey-checkbox">
              <input
                type="checkbox"
                checked={Boolean(f.selectedRequestIds?.includes(item.id))}
                onChange={(event) =>
                  finance({
                    selectedRequestIds: event.target.checked
                      ? [...new Set([...(f.selectedRequestIds ?? []), item.id])]
                      : (f.selectedRequestIds ?? []).filter(
                          (id) => id !== item.id,
                        ),
                  })
                }
              />
              Diesen Darlehensbaustein im Gesamtplan verwenden
            </label>
          ) : (
            <button
              className={f.selectedRequestId === item.id ? 'quiet-button' : ''}
              onClick={() => finance({ selectedRequestId: item.id })}
            >
              {f.selectedRequestId === item.id
                ? 'Im Plan ausgewählt'
                : 'Mit diesen Konditionen planen'}
            </button>
          )}
        </details>
      ))}
      <h3>Private und vermietete Gebäudeteile</h3>
      <p>
        Eine Miete startet erst im eingetragenen Monat und bei bestätigter
        Vermietbarkeit. Flächenanteile beeinflussen die Zuordnung von Zinsen und
        Gebäude-AfA.
      </p>
      {planning.units.map((item) => (
        <details className="journey-unit" key={item.id}>
          <summary>
            <strong>{item.title}</strong>
            <span>
              {item.areaM2} m² ·{' '}
              {item.use === 'private'
                ? 'Eigennutzung'
                : `Vermietung ab Monat ${item.rentalStartMonth}`}
            </span>
          </summary>
          <div className="journey-form-grid">
            <label>
              Gebäudeteil
              <input
                value={item.title}
                onChange={(event) =>
                  unit({ ...item, title: event.target.value })
                }
              />
            </label>
            <label>
              Nutzung
              <select
                value={item.use}
                onChange={(event) =>
                  unit({ ...item, use: event.target.value as UsageUnit['use'] })
                }
              >
                <option value="private">Privat genutzt</option>
                <option value="rental">Vermietet / künftig vermietet</option>
              </select>
            </label>
            <NumberField
              label="Bestätigte Wohnfläche"
              value={item.areaM2}
              onChange={(value) => unit({ ...item, areaM2: value ?? 0 })}
              suffix="m²"
            />
            <NumberField
              label="Kaltmiete monatlich"
              value={item.rentMonthly}
              onChange={(value) => unit({ ...item, rentMonthly: value ?? 0 })}
              suffix="€"
            />
            <NumberField
              label="Mietbeginn Monat nach Kauf"
              value={item.rentalStartMonth}
              onChange={(value) =>
                unit({ ...item, rentalStartMonth: value ?? 0 })
              }
              max={360}
            />
            <NumberField
              label="Mietwachstum jährlich, Annahme"
              value={item.rentGrowthPercent}
              onChange={(value) =>
                unit({ ...item, rentGrowthPercent: value ?? 0 })
              }
              suffix="%"
              max={50}
            />
            <NumberField
              label="Leerstand / Ausfall"
              value={item.vacancyPercent}
              onChange={(value) =>
                unit({ ...item, vacancyPercent: value ?? 0 })
              }
              suffix="%"
              max={100}
            />
            <NumberField
              label="Nicht umlagefähige Kosten monatlich"
              value={item.ownerCostsMonthly}
              onChange={(value) =>
                unit({ ...item, ownerCostsMonthly: value ?? 0 })
              }
              suffix="€"
            />
            <NumberField
              label="Wartungsreserve monatlich"
              value={item.maintenanceReserveMonthly}
              onChange={(value) =>
                unit({ ...item, maintenanceReserveMonthly: value ?? 0 })
              }
              suffix="€"
            />
            <NumberField
              label="Tatsächliche abzugsfähige Ausgaben monatlich"
              value={item.actualDeductibleCostsMonthly}
              onChange={(value) =>
                unit({ ...item, actualDeductibleCostsMonthly: value ?? 0 })
              }
              suffix="€"
            />
            <DocumentSelect
              documents={documents}
              value={item.documentId}
              onChange={(value) => unit({ ...item, documentId: value })}
            />
          </div>
          <label className="journey-checkbox">
            <input
              type="checkbox"
              checked={item.legalRentabilityConfirmed}
              onChange={(event) =>
                unit({
                  ...item,
                  legalRentabilityConfirmed: event.target.checked,
                })
              }
            />
            Vermietbarkeit geprüft; Mietansatz darf in die Planung eingehen.
          </label>
          {item.use === 'rental' && <RentalTaxFields unit={item} documents={documents} onChange={unit}/>}
        </details>
      ))}
      <button
        className="quiet-button"
        onClick={() =>
          onChange({
            ...planning,
            units: [
              ...planning.units,
              {
                id: id(),
                title: 'Weiterer Gebäudeteil',
                areaM2: 0,
                use: 'private',
                rentMonthly: 0,
                rentalStartMonth: 0,
                rentGrowthPercent: 2,
                vacancyPercent: 2,
                ownerCostsMonthly: 0,
                maintenanceReserveMonthly: 0,
                actualDeductibleCostsMonthly: 0,
                legalRentabilityConfirmed: false,
                documentId: '',
              },
            ],
          })
        }
      >
        Gebäudeteil hinzufügen
      </button>
      <details className="journey-tax" open>
        <summary>Steuerannahmen nachvollziehen</summary>
        <div className="journey-form-grid">
          <NumberField
            label="Gebäudeanteil am Kaufwert, Annahme"
            value={f.buildingValueSharePercent}
            onChange={(value) =>
              finance({ buildingValueSharePercent: value ?? 0 })
            }
            suffix="%"
            max={100}
          />
          <NumberField
            label="Fertigstellungsjahr"
            value={f.completionYear}
            onChange={(value) => finance({ completionYear: value })}
            nullable
            max={2100}
          />
          <label>
            Steuermodell
            <select
              value={f.taxMode}
              onChange={(event) =>
                finance({
                  taxMode: event.target.value as Planning['finance']['taxMode'],
                })
              }
            >
              <option value="marginal">Angegebener Grenzsteuersatz</option>
              <option value="tariff2026">Einkommensteuertarif 2026</option>
            </select>
          </label>
          <NumberField
            label="Grenzsteuersatz, Annahme"
            value={f.marginalTaxRate}
            onChange={(value) => finance({ marginalTaxRate: value ?? 0 })}
            suffix="%"
            max={100}
          />
          <NumberField
            label="Zu versteuerndes Einkommen vor Vermietung"
            value={f.taxableIncomeBeforeRental}
            onChange={(value) => finance({ taxableIncomeBeforeRental: value })}
            suffix="€/Jahr"
            nullable
          />
          <NumberField
            label="Planungshorizont Jahre"
            value={f.horizonYears}
            onChange={(value) => finance({ horizonYears: value ?? 1 })}
            max={30}
          />
          <label>
            Zinszuordnung
            <select
              value={f.interestAllocation}
              onChange={(event) =>
                finance({
                  interestAllocation: event.target
                    .value as Planning['finance']['interestAllocation'],
                })
              }
            >
              <option value="area">Flächenanteil als Planungsannahme</option>
              <option value="documented">
                Dokumentierte direkte Zuordnung
              </option>
            </select>
          </label>
          <NumberField
            label="Dokumentierter Mietanteil der Zinsen"
            value={f.rentalInterestSharePercent}
            onChange={(value) =>
              finance({ rentalInterestSharePercent: value ?? 0 })
            }
            suffix="%"
            max={100}
          />
          <DocumentSelect
            label="Beleg für Zinszuordnung"
            documents={documents}
            value={f.allocationDocumentId}
            onChange={(value) => finance({ allocationDocumentId: value })}
          />
        </div>
        <label className="journey-checkbox">
          <input
            type="checkbox"
            checked={f.jointAssessment}
            onChange={(event) =>
              finance({ jointAssessment: event.target.checked })
            }
          />
          Zusammenveranlagung im Tarifmodell
        </label>
        <label className="journey-checkbox">
          <input
            type="checkbox"
            checked={f.allowRentalLossOffset}
            onChange={(event) =>
              finance({ allowRentalLossOffset: event.target.checked })
            }
          />
          Verlustverrechnung als ausdrücklich geprüfte Annahme berücksichtigen
        </label>
        <p className="journey-caption">
          Planungsrechnung für persönliche Eigentümer. Gebäude-AfA ohne
          Bodenwert, Tilgung und bloße Reserven sind keine Werbungskosten.
          Einkommensteuer ohne Soli/Kirchensteuer; der 2026-Tarif wird für
          spätere Jahre als Annahme fortgeschrieben. Individuelle Zuordnung und
          steuerliche Behandlung mit Fachberatung prüfen. Quellen:{' '}
          <a
            href="https://www.gesetze-im-internet.de/estg/__7.html"
            target="_blank"
            rel="noreferrer"
          >
            § 7 EStG
          </a>
          ,{' '}
          <a
            href="https://www.gesetze-im-internet.de/estg/__9.html"
            target="_blank"
            rel="noreferrer"
          >
            § 9
          </a>
          ,{' '}
          <a
            href="https://www.gesetze-im-internet.de/estg/__21.html"
            target="_blank"
            rel="noreferrer"
          >
            § 21
          </a>
          ,{' '}
          <a
            href="https://www.gesetze-im-internet.de/estg/__32a.html"
            target="_blank"
            rel="noreferrer"
          >
            § 32a
          </a>
          .
        </p>
      </details>
      {result.warnings.length > 0 && (
        <ul className="journey-warnings">
          {result.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
      {result.pendingAcquisitionPayments !== null && result.pendingAcquisitionPayments > 0 && <p className="journey-alert">Nach Planende noch zu bezahlen: {money(result.pendingAcquisitionPayments / 100)}. Dieser Betrag ist noch nicht im Haushaltsabfluss enthalten.</p>}
      {!!result.pendingSpecialRepaymentsCents && <p className="journey-alert">Nach Planende vorgesehene Sondertilgungen: {money(result.pendingSpecialRepaymentsCents / 100)}. Noch nicht als Ausgabe berechnet; tatsächliche Restschuld und dann gültige Vertragsrechte bleiben zu prüfen.</p>}
      <h3>Wie sich der Plan entwickelt</h3>
      <div className="journey-projection">
        <table>
          <thead>
            <tr>
              <th>Jahr</th>
              <th>Miete inkl. Umlagen</th>
              <th>Mietanteil</th>
              <th>Abziehbare laufende Kosten</th>
              <th>Abziehbare Mietzinsen</th>
              <th>Abziehbare AfA</th>
              <th>Vermietungsergebnis</th>
              <th>Steuerwirkung</th>
              <th>Ø Darlehenszahlungen / Monat</th><th>Davon Sondertilgung</th><th>Nicht verwendete Sondertilgung</th>
              <th>Haushaltsabfluss</th>
              <th>Spätere Budgetzahlungen</th><th>Spätere Darlehensabrufe</th><th>Bereitstellungszinsen</th><th>Noch nicht abgerufen</th><th>Restdarlehen</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row) => (
              <tr key={row.year}>
                <th>
                  {row.year}
                  {row.refinances.length > 0 && (
                    <small>Anschlussphase: {row.refinances.join(', ')}</small>
                  )}
                </th>
                <td>{money(result.householdShareReady?row.grossRent:null)}</td>
                <td>{Math.round(row.rentalAreaShare * 100)}%</td>
                <td>{money(row.deductibleOperatingCosts)}</td>
                <td>{money(row.deductibleInterest)}</td>
                <td>{money(row.deductibleDepreciation)}</td>
                <td>{money(row.taxableRentalIncome)}</td>
                <td>{money(row.incomeTaxEffect)}</td>
                <td>{money(result.debtReady && result.householdShareReady ? row.debtService / 12 : null)}</td><td>{money(result.debtReady && result.householdShareReady ? row.specialRepayment : null)}</td><td>{money(result.debtReady && result.householdShareReady ? row.unappliedSpecialRepayment : null)}</td>
                <td>{money(row.householdOutflow)}</td>
                <td>{money(result.paymentsReady && result.householdShareReady ? row.acquisitionPayments : null)}</td><td>{money(result.drawsReady && result.householdShareReady ? row.loanDrawReceipts : null)}</td><td>{money(result.drawsReady && result.householdShareReady ? row.commitmentInterest : null)}</td><td>{money(result.drawsReady && result.householdShareReady ? row.undrawnLoan : null)}</td><td>{money(result.debtReady && result.householdShareReady ? row.remainingLoan : null)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="journey-caption">
        Parallel verglichene Objekte sind zunächst Alternativen. Eigenkapital
        darf bei einem gemeinsamen Kauf mehrerer Objekte nicht mehrfach
        eingeplant werden.
      </p>
    </section>
  );
}
